import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Building2, Loader2, Mail, XCircle, User, Phone, KeyRound, LogOut,
  Eye, EyeOff,
} from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

/**
 * Proposal A — the ONLY way to accept a CARTAL employee invitation.
 *
 * Rendered at:  /invite/accept?token=<opaque-token>
 *
 * Flow:
 *   1. GET  /platform/invites/:token          → fetch invite metadata
 *   2. If the user is already signed in (any account) → we refuse to
 *      accept: an existing account MUST NOT be linked to the business
 *      via an invitation. We show a sign-out CTA.
 *   3. Otherwise render an inline registration form (name / phone /
 *      password). The invited email is fixed and read-only.
 *   4. POST /platform/invites/:token/accept-register → creates the new
 *      CARTAL account, links it to the business, sets auth cookies,
 *      returns the business_id we navigate into.
 *
 * There is intentionally NO "قبول الدعوة" button. Successful registration
 * IS the acceptance of the invitation.
 */
const roleLabel = (k) => ({
  owner: "مالك", co_owner: "مالك مشارك", branch_manager: "مدير فرع",
  employee: "موظف", accountant: "محاسب", viewer: "مطّلع",
}[k] || k);

export default function InviteAcceptPage() {
  const { lang } = useI18n();
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState({ loading: true, invite: null, error: null });

  // Registration form state.
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Eye-toggle state — the two password inputs are independent so the
  // user can, for example, reveal only the confirm field to verify a
  // typo without exposing the primary field. The buttons live INSIDE
  // the input via absolute positioning + a matching padding on the
  // input; they never overlap letters and never cover other page
  // elements. Uses lucid-react eye icons so iOS/Safari renders them
  // identically to Chrome/Android.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, invite: null, error: lang === "ar" ? "الرابط غير صالح." : "Invalid link." });
      return;
    }
    apiClient.get(`/platform/invites/${encodeURIComponent(token)}`)
      .then(({ data }) => setState({ loading: false, invite: data, error: null }))
      .catch((err) => {
        const d = err?.response?.data?.detail;
        const msg = (typeof d === "object" && d?.[lang]) || (typeof d === "string" && d)
          || (lang === "ar" ? "الدعوة غير موجودة أو انتهت." : "Invite not found or expired.");
        setState({ loading: false, invite: null, error: msg });
      });
  }, [token, lang]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim()) {
      toast.error(lang === "ar" ? "الاسم مطلوب." : "Name is required.");
      return;
    }
    if (password.length < 8) {
      toast.error(lang === "ar" ? "كلمة المرور يجب أن لا تقل عن 8 أحرف." : "Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error(lang === "ar" ? "كلمة المرور غير متطابقة." : "Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(
        `/platform/invites/${encodeURIComponent(token)}/accept-register`,
        { name: name.trim(), phone: phone.trim() || undefined, password },
      );
      // Fresh account, same policy as self-signup: nudge the user to
      // share their city on first login. The city-onboarding modal
      // mounted at the app root reads this flag and — because the new
      // member's `user.city` is unset — will render the location
      // request exactly once. business_member accounts get the same
      // prompt as any other new account; source-of-registration is
      // NOT a reason to skip the location gate.
      try { localStorage.setItem("cartal-city-onboard-pending", "1"); } catch { /* ignore */ }
      toast.success(lang === "ar" ? "تم إنشاء الحساب وقبول الدعوة." : "Account created and invitation accepted.");
      await refresh(); // pull the fresh session into the AuthProvider
      navigate(`/business/${data.business_id}`, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (typeof d === "object" && d?.[lang]) || (typeof d === "string" && d)
        || formatApiError(d)
        || (lang === "ar" ? "تعذّر إنشاء الحساب." : "Could not create the account.");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const signOutAndReload = async () => {
    await logout();
    // Refresh the page so we land on the unauthenticated register form.
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#fafaf7] py-12 px-4 flex items-center justify-center">
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[#0a0a0a]/[0.06] shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-2 text-[#8a6f2e]">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-[11px] font-bold tracking-widest uppercase">
            {lang === "ar" ? "دعوة انضمام إلى كارتال" : "CARTAL invitation"}
          </span>
        </div>
        <h1 className="text-2xl font-bold text-[#0a0a0a] mb-4">
          {lang === "ar" ? "إنشاء حساب وقبول الدعوة" : "Create account & accept"}
        </h1>

        {state.loading ? (
          <div className="py-10 flex items-center justify-center text-[#8a8a8a]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : state.error || !state.invite ? (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 p-4 flex items-start gap-2"
               data-testid="invite-error">
            <XCircle className="w-5 h-5 mt-0.5" />
            <div className="text-sm leading-6">{state.error}</div>
          </div>
        ) : (
          <>
            <InviteSummary invite={state.invite} lang={lang} />

            {state.invite.status !== "pending" ? (
              <div className="rounded-xl bg-yellow-50 border border-yellow-300 text-yellow-800 p-4 text-sm"
                   data-testid="invite-not-pending">
                {state.invite.status === "expired"
                  ? (lang === "ar" ? "انتهت صلاحية الدعوة. اطلب من صاحب المعرض إعادة الإرسال." : "This invitation has expired. Ask the business owner to resend.")
                  : (lang === "ar" ? "الدعوة لم تعد صالحة." : "This invitation is no longer valid.")}
              </div>
            ) : user ? (
              // Any authenticated user (matching email or not) must sign out —
              // Proposal A forbids linking an existing account to a business.
              <div className="space-y-3" data-testid="invite-must-sign-out">
                <div className="rounded-xl bg-yellow-50 border border-yellow-300 text-yellow-800 p-4 text-sm leading-7">
                  {lang === "ar"
                    ? `أنت مسجّل الدخول بحساب كارتال قائم (${user.email}). لأسباب أمنية لا يمكن ربط حساب قائم بالنشاط عبر دعوة موظف. سجّل الخروج ثم أعد فتح الرابط لإنشاء حساب جديد بالبريد ${state.invite.email}.`
                    : `You are signed in with an existing CARTAL account (${user.email}). For safety, an existing account cannot be linked to a business through an employee invitation. Sign out and re-open this link to create a new account for ${state.invite.email}.`}
                </div>
                <button
                  type="button"
                  onClick={signOutAndReload}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold border border-[#0a0a0a]/10 bg-white hover:bg-[#faf7ee]"
                  data-testid="invite-sign-out-btn">
                  <LogOut className="w-4 h-4" /> {lang === "ar" ? "تسجيل الخروج" : "Sign out"}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="invite-register-form">
                <p className="text-sm text-[#525252] leading-7">
                  {lang === "ar"
                    ? "لإكمال قبول الدعوة، أنشئ حساب كارتال جديد بنفس البريد المُدعى."
                    : "To accept, create a new CARTAL account with the invited email."}
                </p>

                <Field icon={<Mail className="w-4 h-4" />}
                       label={lang === "ar" ? "البريد المُدعى" : "Invited email"}>
                  <input
                    type="email"
                    value={state.invite.email}
                    readOnly
                    className="w-full bg-[#faf7ee] rounded-lg px-3 py-2 text-sm font-semibold text-[#0a0a0a]/80"
                    data-testid="invite-email-readonly"
                  />
                </Field>

                <Field icon={<User className="w-4 h-4" />}
                       label={lang === "ar" ? "الاسم الكامل" : "Full name"}>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-lg border border-[#0a0a0a]/10 px-3 py-2 text-sm"
                    placeholder={lang === "ar" ? "الاسم كما تريد أن يظهر" : "Your name"}
                    data-testid="invite-name-input"
                  />
                </Field>

                <Field icon={<Phone className="w-4 h-4" />}
                       label={lang === "ar" ? "رقم الجوال (اختياري)" : "Mobile (optional)"}>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-lg border border-[#0a0a0a]/10 px-3 py-2 text-sm"
                    placeholder="+9665XXXXXXXX"
                    dir="ltr"
                    data-testid="invite-phone-input"
                  />
                </Field>

                <Field icon={<KeyRound className="w-4 h-4" />}
                       label={lang === "ar" ? "كلمة المرور" : "Password"}>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-lg border border-[#0a0a0a]/10 px-3 py-2 text-sm ps-10"
                      placeholder={lang === "ar" ? "٨ أحرف على الأقل" : "At least 8 characters"}
                      autoComplete="new-password"
                      data-testid="invite-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 start-2 flex items-center text-[#8a8a8a] hover:text-[#0a0a0a] px-1"
                      aria-label={showPassword
                        ? (lang === "ar" ? "إخفاء كلمة المرور" : "Hide password")
                        : (lang === "ar" ? "إظهار كلمة المرور" : "Show password")}
                      tabIndex={-1}
                      data-testid="invite-password-toggle">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <Field icon={<KeyRound className="w-4 h-4" />}
                       label={lang === "ar" ? "تأكيد كلمة المرور" : "Confirm password"}>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full rounded-lg border border-[#0a0a0a]/10 px-3 py-2 text-sm ps-10"
                      autoComplete="new-password"
                      data-testid="invite-password-confirm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute inset-y-0 start-2 flex items-center text-[#8a8a8a] hover:text-[#0a0a0a] px-1"
                      aria-label={showConfirm
                        ? (lang === "ar" ? "إخفاء التأكيد" : "Hide confirm")
                        : (lang === "ar" ? "إظهار التأكيد" : "Show confirm")}
                      tabIndex={-1}
                      data-testid="invite-confirm-toggle">
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold shadow-sm disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                  data-testid="invite-submit-btn">
                  {submitting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ShieldCheck className="w-4 h-4" />}
                  {lang === "ar" ? "إنشاء الحساب وقبول الدعوة" : "Create account & accept"}
                </button>

                <p className="text-[11px] text-[#8a8a8a] leading-6 pt-2">
                  {lang === "ar"
                    ? "بإنشاء الحساب تفعّل ملفك في كارتال وتنضم مباشرة إلى النشاط المدعوّ منه بالدور المحدَّد. لا تحتاج خطوة قبول إضافية."
                    : "Creating the account activates your CARTAL profile and joins the inviting business with the specified role. No extra accept step is required."}
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InviteSummary({ invite, lang }) {
  return (
    <div className="rounded-xl bg-[#faf7ee] border border-[#c9a55c]/25 p-4 mb-4 space-y-2 text-sm"
         data-testid="invite-summary">
      <Row icon={<Building2 className="w-4 h-4" />} label={lang === "ar" ? "النشاط التجاري" : "Business"}
           value={invite.business?.name_ar || invite.business?.name_en || "—"} />
      <Row icon={<ShieldCheck className="w-4 h-4" />} label={lang === "ar" ? "الدور" : "Role"}
           value={roleLabel(invite.role_key)} />
      {invite.title_ar
        ? <Row icon={<ShieldCheck className="w-4 h-4" />} label={lang === "ar" ? "المسمى الوظيفي" : "Title"}
               value={invite.title_ar} />
        : null}
      <Row icon={<Mail className="w-4 h-4" />} label={lang === "ar" ? "البريد المُدعى" : "Invited email"}
           value={invite.email} />
    </div>
  );
}

function Field({ icon, label, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#8a6f2e] mb-1">
        {icon}{label}
      </span>
      {children}
    </label>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#8a6f2e] mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-[#8a8a8a]">{label}</div>
        <div className="text-sm font-semibold text-[#0a0a0a] break-all">{value}</div>
      </div>
    </div>
  );
}
